// @flow
import ResourceModel from "../base/ResourceModel";
import ConceptRelationCollection from "./ConceptRelationCollection";
import SourceReferenceCollection from "./SourceReferenceCollection";
import ConceptTypeDetailModel from "./ConceptTypeDetailModel";

import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";

import type {
  ModularUIModel,
  labelsJSON,
  textfragmentJSON,
  propertyJSON,
} from "../types";
import type { ModularUIResponse } from "../../modularui";
import type Href from "../href/Href";
import type LinkModel from "../links/LinkModel";
import type LinkCollection from "../links/LinkCollection";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Model for concept details, available through modelcatalog
 */
export default class ConceptDetailModel extends ResourceModel {
  _relations: ConceptRelationCollection;
  _conceptType: ?ConceptTypeDetailModel;
  _sourceReferences: SourceReferenceCollection;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this._relations = new ConceptRelationCollection(
      this.data.relations,
      this.entryDate,
      this.modelOptions,
    );
  }

  /**
   */
  get type(): string {
    return "ConceptDetail";
  }

  /**
   */
  static get modelName(): string {
    return "ConceptDetailModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "ConceptDetail"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const conceptTypeLink = this.links.getLinkByKey("concepttype");
    const relationsCollectionLinks =
      this.relationsCollection.getInitialChildModelLinks();

    if (conceptTypeLink) {
      conceptTypeLink.isCacheable = true;
      return [conceptTypeLink, ...relationsCollectionLinks];
    }

    return relationsCollectionLinks;
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    // $FlowExpectedError[incompatible-type]
    const conceptTypeModel: ?ConceptTypeDetailModel = models.find(
      (model) => model instanceof ConceptTypeDetailModel,
    );

    if (conceptTypeModel) {
      this.conceptType = conceptTypeModel;
    }

    this.relationsCollection.setChildModels(models, errors);
  }

  /**
   * Retrieve concept detail identifier as key for this model
   */
  get key(): string {
    return this.getData("_id", "concept");
  }

  /**
   * Getting the self link of this Concept
   */
  get selfhref(): Href {
    const { href } = this.selflink;

    if (this.entryDate) {
      href.setParameter(TIMEVERSION_FILTER_NAME, this.entryDate);
    } else {
      href.removeParameter(TIMEVERSION_FILTER_NAME);
    }

    return href;
  }

  /**
   * Available diagrams for the concept, most of the time just one
   */
  get diagramLinks(): LinkCollection {
    return this.links.getLinksByGroup("diagram");
  }

  /**
   * Get conceptType of concept
   */
  get conceptType(): ?ConceptTypeDetailModel {
    return this._conceptType;
  }

  /**
   */
  set conceptType(conceptType: ?ConceptTypeDetailModel) {
    this._conceptType = conceptType;
  }

  /**
   * Get concept label
   */
  get label(): string {
    return this.data.conceptLabel;
  }

  /**
   * Get model category of the concept
   */
  get modelCategory(): string {
    return this.getData("modelCategory", "");
  }

  /**
   * Get taxonomy type
   */
  get taxonomyType(): string {
    return this.getData("taxonomyType", "default");
  }

  /**
   * Get concept relations collection
   */
  get relationsCollection(): ConceptRelationCollection {
    return this._relations;
  }

  /**
   * Get concept formula
   */
  get formula(): string {
    return this.data.formula;
  }

  /**
   * @private
   */
  getElements<T>(propName: string, typePropName: string): Array<T> {
    // $FlowIssue
    const elementTypes = this.conceptType?.[typePropName] ?? [];
    const allElements = this.data[propName] ?? [];

    const elements = [];
    const usedElementTypes = [];
    for (const element of allElements) {
      const elementType = elementTypes.find(
        (elType) => elType._id === element.type,
      );
      if (elementType) {
        elements.push({ ...elementType, ...element });
        usedElementTypes.push(elementType._id);
      } else {
        elements.push(element);
      }
    }

    for (const elementType of elementTypes) {
      if (!usedElementTypes.includes(elementType._id)) {
        elements.push(elementType);
      }
    }

    return elements;
  }

  /**
   * Get additional labels of concept
   */
  get labels(): Array<labelsJSON> {
    return this.getElements<labelsJSON>("labels", "labelTypes");
  }

  /**
   * Get label elements by id
   */
  getLabelElementByIds(ids: Array<string>): Array<labelsJSON> {
    return this.labels.filter((label: labelsJSON) => ids.includes(label._id));
  }

  /**
   * Get concept properties
   */
  get conceptProperties(): Array<propertyJSON> {
    return this.getElements<propertyJSON>("properties", "propertyTypes");
  }

  /**
   * Get concept properties by id
   */
  getConceptPropertiesByIds(ids: Array<string>): Array<propertyJSON> {
    return this.conceptProperties.filter((property) =>
      ids.includes(property._id),
    );
  }

  /**
   * Get Text fragments
   */
  get textfragments(): Array<textfragmentJSON> {
    return this.getElements<textfragmentJSON>(
      "textFragments",
      "textFragmentTypes",
    );
  }

  /**
   * Get text fragments by id
   */
  getTextFragmentByKeys(keys: Array<string>): Array<textfragmentJSON> {
    return this.textfragments.filter((textfragment) =>
      keys.includes(textfragment.type),
    );
  }

  /**
   * Get source reference collection
   */
  getSourceReferenceCollection(
    availableLocales: Array<string> = [],
  ): SourceReferenceCollection {
    if (!this._sourceReferences) {
      let sectionReferenceTypes = [];
      if (this.conceptType?.sectionReferenceTypes) {
        sectionReferenceTypes = this.conceptType.sectionReferenceTypes;
      }

      this._sourceReferences = new SourceReferenceCollection(
        this.getSourceReferencesForCurrentLanguage(availableLocales),
        sectionReferenceTypes,
        this.entryDate,
        this.modelOptions,
      );
    }

    return this._sourceReferences;
  }

  /*
   * Retrieve all sourceReferenceTypes that are valid for the current language
   * Used by sourceRef collection
   */
  /**
   */
  getSourceReferencesForCurrentLanguage(
    availableLocales: Array<string>,
  ): Array<Object> {
    const LANGUAGE_POSTFIX_LENGTH = 3;
    if (this.data.sourceReferences) {
      const availableLanguagesInSourceReferences =
        this.data.sourceReferences.map((sourceReference) =>
          sourceReference.type.substring(
            sourceReference.type.length - LANGUAGE_POSTFIX_LENGTH,
          ),
        );

      const currentLanguagePostfix = `_${this.locale}`;

      if (
        availableLanguagesInSourceReferences.includes(currentLanguagePostfix)
      ) {
        // return all sourceReferences that end with language that is selected
        return this.data.sourceReferences.filter((sourceReference) =>
          sourceReference.type.endsWith(currentLanguagePostfix),
        );
      }

      const availableLanguages = availableLocales.map(
        (locale) => `_${locale.split("-")[0]}`,
      );

      // return all sourceReferences that do not end with language postfix
      return this.data.sourceReferences.filter(
        (sourceReference) =>
          !availableLanguages.includes(
            sourceReference.type.substring(
              sourceReference.type.length - LANGUAGE_POSTFIX_LENGTH,
            ),
          ),
      );
    }
    return [];
  }

  /**
   * Retrieve modelcatalog.js
   */
  get entryDate(): string | null {
    return this.data.filter?.[TIMEVERSION_FILTER_NAME]?.value ?? null;
  }

  /**
   * Indicates if the given concept type id or href exists in the hierarchy of concept types for this concept
   */
  isOfConceptType(conceptTypeId: string): boolean {
    return this.conceptType?.isOfConceptType(conceptTypeId) ?? false;
  }
}
